import { useState } from "react";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import DarkButton from "components/actions/DarkButton";
import DatePicker from "components/fields/DatePicker";
import WhiteButton from "components/actions/WhiteButton";
import PlusIcon from "components/icons/PlusIcon";
import PenEditIcon from "components/icons/PenEditIcon";
import Dialog from "components/dialog";
import Checkbox from "components/checkbox";
import { useDisclosure } from "@chakra-ui/hooks";
import UploadIcon from "components/icons/UploadIcon";
import Avatar from "components/avatar";

const AllergiesArray = [
  "Milk",
  "Eggs",
  "Peanuts",
  "Tree nuts (e.g., almonds, walnuts)",
  "Wheat",
  "Soy",
  "Fish",
  "Shellfish (e.g., shrimp, crab)",
  "Sesame",
  "Mustard",
];

const NewChildForm = ({ onSubmit, errors }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [user, setUser] = useState({});
  const [avatar, setAvatar] = useState();

  const handleFileChange = (e) => {
    if (e.target.files) {
      setAvatar(e.target.files[0]);
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (avatar) {
      onSubmit({ ...user, role: "child", avatar: avatar });
    } else {
      onSubmit({ ...user, role: "child" });
    }
  };

  // date format set through mac book MM/DD/YYYY
  function formatToMMDDYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  return (
    <>
      <form
        onSubmit={onFormSubmit}
        noValidate={true}
        className="flex flex-col gap-6 px-4 py-2"
      >
        <div className="flex flex-col gap-4 mb-4">
          <p className="text-base">Child details</p>
          <InputField
            placeholder="Child first name"
            value={user.firstName || ""}
            onChange={(e) => setUser({ ...user, firstName: e.target.value })}
            state={
              errors.findIndex((error) => error.path === "firstName") !== -1
                ? "error"
                : ""
            }
          />
          <InputField
            placeholder="Child last name"
            value={user.lastName || ""}
            onChange={(e) => setUser({ ...user, lastName: e.target.value })}
            state={
              errors.findIndex((error) => error.path === "lastName") !== -1
                ? "error"
                : ""
            }
          />
          <Select
            placeholder="Child gender"
            value={user.gender || ""}
            onChange={(e) => setUser({ ...user, gender: e.target.value })}
            state={
              errors.findIndex((error) => error.path === "gender") !== -1
                ? "error"
                : ""
            }
            required
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Non-binary</option>
            <option value="na">Prefer not to tell</option>
          </Select>
          {/* <DatePicker placeholder="Date of birth"
                    value={user.dob}
                    onChange={(v) => {setUser({...user, dob: v.toLocaleDateString(['en-US'])})}}
                    state={errors.findIndex(error => error.path === 'dob') !== -1 ? "error" : ""}/> */}

          <DatePicker
            placeholder="Date of birth"
            value={formatToMMDDYYYY(user.dob)}
            onChange={(v) => {
              setUser({ ...user, dob: formatToMMDDYYYY(v) });
            }}
            state={
              errors.findIndex((error) => error.path === "dob") !== -1
                ? "error"
                : ""
            }
          />

          <Select
            placeholder="Child grade"
            value={user.grade || ""}
            onChange={(e) => setUser({ ...user, grade: e.target.value })}
            state={
              errors.findIndex((error) => error.path === "grade") !== -1
                ? "error"
                : ""
            }
            required
          >
            <option value="1">Preschool</option>
            <option value="2">Pre-kindergarten</option>
            <option value="3">Kindergarten</option>
            <option value="4">1st Grade</option>
            <option value="5">2nd Grade</option>
            <option value="6">3rd Grade</option>
            <option value="7">4th Grade</option>
            <option value="8">5th Grade</option>
            <option value="9">6th Grade</option>
            <option value="10">7th Grade</option>
            <option value="11">8th Grade</option>
          </Select>

          <p className="text-base">Child profile photo</p>
          <div>
            {avatar ? (
              <img
                src={URL.createObjectURL(avatar)}
                alt={`${user.firstName} ${user.lastName}`}
                className="h-[72px] w-[72px] rounded-full inline-block mr-2 m-auto"
              />
            ) : (
              user.thumb && <Avatar user={user} />
            )}

            <div
              className={`inline-block relative rounded-2xl ${
                errors.findIndex((error) => error.path === "avatar") !== -1
                  ? "border border-red-500"
                  : ""
              }`}
            >
              <input
                className="block absolute left-0 top-0 w-full h-full z-[1] bg-gray-800 rounded-2xl opacity-0"
                type="file"
                onChange={handleFileChange}
                accept="image/*"
              />
              <WhiteButton type="button">
                <span className="inline-block align-middle mr-2">
                  <UploadIcon />
                </span>
                <span className="inline-block align-middle">Upload</span>
              </WhiteButton>
            </div>
          </div>

          <p className="text-base">Child food allergies</p>
          <div>
            {(user.allergies || []).length === 0 && (
              <WhiteButton onClick={onOpen} type="button">
                <span className="inline-block align-middle mr-2">
                  <PlusIcon />
                </span>
                <span className="inline-block align-middle">Add</span>
              </WhiteButton>
            )}
            {(user.allergies || []).length > 0 && (
              <div className="rounded-3xl md:rounded-2xl p-4 bg-white dark:bg-navy-700 mb-4">
                {(user.allergies || [])
                  .map((k) => AllergiesArray[k])
                  .filter((x) => x)
                  .join(", ")}
                {/* {(user.allergies || []).map((tag, k) => <span key={'tag'+k} className='no-wrap'>{tag}</span>)} */}
              </div>
            )}
            {(user.allergies || []).length > 0 && (
              <WhiteButton onClick={onOpen} type="button">
                <span className="inline-block align-middle mr-2">
                  <PenEditIcon />
                </span>
                <span className="inline-block align-middle">Edit</span>
              </WhiteButton>
            )}
          </div>
        </div>

        <DarkButton type="submit">Confirm</DarkButton>
      </form>

      <Dialog isOpen={isOpen} onClose={onClose}>
        <h2 className="text-lg font-bold">Add food allergens</h2>
        {AllergiesArray.map((tag, k) => {
          return (
            <div key={k} className="flex flex-row gap-2 items-center">
              <Checkbox
                id={"allerge" + k}
                checked={(user.allergies || []).indexOf(k) !== -1}
                onClick={(e) =>
                  e.target.checked
                    ? setUser({
                        ...user,
                        allergies: (user.allergies || [])
                          .filter((a) => a !== k)
                          .concat([k]),
                      })
                    : setUser({
                        ...user,
                        allergies: (user.allergies || []).filter(
                          (a) => a !== k
                        ),
                      })
                }
              />
              <label for={"allerge" + k} className="flex-1 py-2.5">
                {tag}
              </label>
            </div>
          );
        })}
      </Dialog>
    </>
  );
};

NewChildForm.AllergiesArray = AllergiesArray;

export default NewChildForm;
